// Redux/Slices/Pages/Client/WorkspaceClassSlice.js
// Manages: Global Expert Personas (Electrician, Salon, etc.)
// BZV2 backend: GET /api/v2/catalog/workspace-classes

import { createSlice } from '@reduxjs/toolkit';
import { plainAxios } from '../../../../Common/Interceptors/AxiosConfig';

const SESSION_KEY = 'bz_workspace_classes';
const CACHE_DURATION = 15 * 60 * 1000; // 15 Minutes for Taxonomy

const initialState = {
  allClasses: [],
  isLoading: false,
  errors: null,
  lastFetchedAt: null,
};

const workspaceClassSlice = createSlice({
  name: 'workspaceClasses',
  initialState,
  reducers: {
    setAllClasses: (state, action) => {
      state.allClasses = action.payload;
      state.lastFetchedAt = Date.now();
      state.errors = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setErrors: (state, action) => {
      // Ensure error is always a string or null to prevent React rendering crashes (Error #31)
      state.errors = typeof action.payload === 'string' 
        ? action.payload 
        : (action.payload?.message || "An unexpected error occurred");
    },
    clearErrors: (state) => {
      state.errors = null;
    }
  },
});

export const { setAllClasses, setLoading, setErrors, clearErrors } = workspaceClassSlice.actions;

// ── Thunks (Manual Pattern) ──────────────────────────────────────────────────

/** 
 * Fetch all active industrial personas (Workspace Classes) 
 * Maps to backend: GET /api/v2/catalog/workspace-classes 
 */
export const fetchWorkspaceClasses = () => async (dispatch, getState) => {
  try {
    // Check Session Cache
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        dispatch(setAllClasses(data));
        return;
      }
    }

    dispatch(setLoading(true));
    dispatch(setErrors(null));

    const response = await plainAxios.get('/catalog/workspace-classes');
    const { success, data, message } = response?.data || {};

    if (success) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        data: data,
        timestamp: Date.now()
      }));
      dispatch(setAllClasses(data));
    } else {
      dispatch(setErrors(message || 'Failed to load industrial personas'));
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Network error';
    dispatch(setErrors(errorMsg));
  } finally {
    dispatch(setLoading(false));
  }
};

export default workspaceClassSlice.reducer;
